// Связь с сервером: POST /api/<действие> {args} → {ok, data | error}. call() — с «загружается» и сообщением об ошибке.
import type { AppState, Settings } from '@praktika/core';
import { $, busy, toast } from './dom.ts';
import { render } from './nav.ts';
import { S, UI, setS } from './state.ts';

// Снимки экранов: застывшее «сейчас» передаётся серверу (он учитывает его только в режиме проверки).
export const testClock: { now: string | null } = { now: null };

// Ошибка сервера: code 'login' — нужно войти, 'no_practice' — вошёл, но кабинета нет (email — кто вошёл),
// 'google' — доступ к календарю пропал.
export class ApiError extends Error {
  code: string;
  email: string;
  constructor(message: string, code = '', email = '') {
    super(message);
    this.code = code;
    this.email = email;
  }
}

// Что показать, когда сервер не пускает: задаёт login.ts (чтобы не было круговой зависимости).
let onAccess: (e: ApiError) => void = () => {};
export function setAccessHandler(fn: (e: ApiError) => void): void { onAccess = fn; }

export async function rpc<T = unknown>(name: string, ...args: unknown[]): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (testClock.now) headers['x-test-now'] = testClock.now;
  let r: Response;
  try {
    r = await fetch('/api/' + name, { method: 'POST', headers, body: JSON.stringify({ args }),
      credentials: 'same-origin' });
  } catch {
    throw new ApiError('Нет связи с сервером');
  }
  let body: { ok?: boolean; data?: T; error?: string; code?: string; email?: string };
  try { body = await r.json(); } catch { throw new ApiError('Непонятный ответ сервера'); }
  if (!r.ok || !body.ok) {
    const e = new ApiError(body.error || 'Ошибка', body.code, body.email);
    if (e.code === 'login' || e.code === 'no_practice') onAccess(e);
    throw e;
  }
  return body.data as T;
}

export async function call<T = unknown>(name: string, ...args: unknown[]): Promise<T> {
  busy(true);
  try {
    return await rpc<T>(name, ...args);
  } catch (e) {
    const code = (e as ApiError).code;
    if (code !== 'login' && code !== 'no_practice') toast((e as Error).message, true);
    throw e;
  } finally {
    busy(false);
  }
}

// тема: задаёт theme.ts (чтобы не было круговой зависимости)
let themeFn: (mode: string) => void = () => {};
export function setThemeApplier(fn: (mode: string) => void): void { themeFn = fn; }

export async function reload(): Promise<AppState | void> {
  try {
    const s = await rpc<AppState>('state');
    setS(s);
    if (s?.settings) themeFn(s.settings.theme || 'system');
    render();
    return s;
  } catch (e) {
    const code = (e as ApiError).code;
    if (code !== 'login' && code !== 'no_practice') toast((e as Error).message, true);
  }
}

export async function afterChange(msg?: string): Promise<void> {
  await reload();
  if (msg) toast(msg);
}

// Перечитать, когда человек ничего не заполняет: открыто окно снизу или идёт ввод — подождать (до двух минут).
export function reloadWhenFree(note?: string, tries = 0): void {
  const ae = document.activeElement, typing = !!ae && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName);
  if (!$('#sheet').hidden || typing) {
    if (tries < 40) setTimeout(() => reloadWhenFree(note, tries + 1), 3000);
    return;
  }
  void reload().then(() => { if (note) toast(note); });
}

type SettingsPatch = Partial<Record<keyof Settings, unknown>>;

// Настройка применяется на экране сразу, запись уходит следом; при ошибке — откат.
export function saveSettings(map: SettingsPatch, msg?: string): Promise<void> {
  const prev: Record<string, unknown> = {}, cur = S.settings as unknown as Record<string, unknown>;
  for (const [k, v] of Object.entries(map)) { prev[k] = cur[k]; cur[k] = v; }
  if (map.bg_light !== undefined || map.bg_dark !== undefined) themeFn(S.settings.theme || 'system');
  render();
  return rpc('saveSettings', map).then(() => { toast(msg || 'Сохранено'); return reload().then(() => undefined); },
    (e: Error) => { for (const [k, v] of Object.entries(prev)) cur[k] = v; render(); toast(e.message, true); });
}

// Настройка вида: сразу на экране, на сервер — без сообщения и без перечитывания данных.
export function saveSettingsQuiet(map: SettingsPatch): Promise<void> {
  const prev: Record<string, unknown> = {}, cur = S.settings as unknown as Record<string, unknown>;
  for (const [k, v] of Object.entries(map)) { prev[k] = cur[k]; cur[k] = v; }
  render();
  return rpc('saveSettings', map).then(() => undefined, (e: Error) => {
    for (const [k, v] of Object.entries(prev)) cur[k] = v;
    render();
    toast(e.message, true);
  });
}

// Деньги за месяц: перечитать при следующем открытии вкладки.
export function dropMoney(): void { UI.money = null; }
